var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { InputAdornment, Menu, MenuItem, styled, TextField, } from '@mui/material';
import { Time16 } from '@yunex/yds-icons';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState, } from 'react';
import { Rifm } from 'rifm';
import Button from '../Button/Button';
import { rem } from '../../utils/rem';
import dayjs from 'dayjs';
import { useUtils } from '@mui/x-date-pickers/internals';
var SMenu = styled(Menu)({ maxHeight: rem(220), marginTop: 2 });
var TimePicker = function (_a) {
    var inputValue = _a.value, onChange = _a.onChange, _b = _a.steps, steps = _b === void 0 ? 60 : _b, // In minutes.
    ampmProp = _a.ampm, mask = _a.mask, _c = _a.maskChar, maskChar = _c === void 0 ? '_' : _c, _d = _a.InputProps, InputProps = _d === void 0 ? {} : _d, _e = _a.MenuProps, MenuProps = _e === void 0 ? {} : _e, _f = _a.InputAdornmentIcon, InputAdornmentIcon = _f === void 0 ? Time16 : _f, onKeyDown = _a.onKeyDown, showSeconds = _a.showSeconds, _g = _a.step, step = _g === void 0 ? 3600000 : _g, minTime = _a.minTime, maxTime = _a.maxTime, customTimes = _a.times, props = __rest(_a, ["value", "onChange", "steps", "ampm", "mask", "maskChar", "InputProps", "MenuProps", "InputAdornmentIcon", "onKeyDown", "showSeconds", "step", "minTime", "maxTime", "times"]);
    // Hooks
    var utils = useUtils();
    var ampm = ampmProp == undefined ? utils.is12HourCycleInCurrentLocale() : ampmProp;
    var _h = useState(), anchorElement = _h[0], setAnchorElement = _h[1];
    var _j = useState(0), anchorElementWidth = _j[0], setAnchorElementWidth = _j[1];
    var handleOpen = function (evt) {
        // @ts-ignore
        setAnchorElement(evt.currentTarget.parentElement.nextElementSibling);
        setAnchorElementWidth(
        // @ts-ignore
        evt.currentTarget.parentElement.nextElementSibling.getBoundingClientRect().width);
    };
    var handleClose = function () {
        setAnchorElement(undefined);
    };
    var align12hFormat = function (timeToAlign) {
        var hours = timeToAlign.split(':')[0];
        var withLeadingZero = hours.length === 2 ? timeToAlign : '0'.concat(timeToAlign);
        return "".concat(withLeadingZero.substring(0, showSeconds ? 8 : 5), " ").concat(withLeadingZero.substring(withLeadingZero.length - 2));
    };
    var handleKeyDown = function (event) {
        var startTime = Number.isNaN(new Date("1/1/2021 ".concat(inputValue)).getTime())
            ? new Date("1/1/2021 00:00")
            : new Date("1/1/2021 ".concat(inputValue));
        var isDownKey = event.key === 'ArrowDown';
        var isUpKey = event.key === 'ArrowUp';
        if (isDownKey || isUpKey) {
            var endTime = new Date(startTime.getTime() + (isDownKey ? -step : step));
            var endTimeString = ampm
                ? align12hFormat(endTime.toLocaleTimeString('en-US'))
                : endTime.toTimeString().substring(0, showSeconds ? 8 : 5);
            onChange(endTimeString, event);
        }
    };
    var handleMenuClick = useCallback(function (time) { return function (event) {
        handleClose();
        onChange(time, event);
    }; }, []);
    var refuse = ampm ? /[^\dap]+/gi : /[^\d]+/gi;
    var format = "".concat(ampm ? utils.formats.hours12h : utils.formats.hours24h, ":").concat(utils.formats.minutes);
    if (showSeconds) {
        format += ':' + utils.formats.seconds;
    }
    if (ampm) {
        format += ' A';
    }
    var inputMask = mask;
    if (!inputMask) {
        inputMask = "".concat(maskChar).concat(maskChar, ":").concat(maskChar).concat(maskChar);
        if (showSeconds) {
            inputMask += ":".concat(maskChar).concat(maskChar);
        }
        if (ampm) {
            inputMask += ' _M';
        }
    }
    var formatter = useCallback(function (value) {
        var result = '';
        var parsed = value.replace(refuse, '');
        if (parsed === '') {
            return parsed;
        }
        var i = 0;
        var n = 0;
        while (i < inputMask.length) {
            var currentMaskChar = inputMask[i];
            if (currentMaskChar === maskChar && n < parsed.length) {
                var parsedChar = parsed[n];
                result += parsedChar;
                n += 1;
            }
            else {
                result += currentMaskChar;
            }
            i += 1;
        }
        return result;
    }, [inputMask, maskChar, refuse]);
    var times = useMemo(function () {
        if (customTimes)
            return customTimes;
        if (!utils)
            return [];
        var today = dayjs();
        var startTime = minTime || utils.startOfDay(today);
        var endTime = maxTime || utils.endOfDay(today);
        var dates = [];
        var currentTime = startTime;
        // @ts-ignore
        while (currentTime <= endTime) {
            if (currentTime) {
                dates.push(dayjs(currentTime).format(format));
                var currentMinutes = utils.getMinutes(currentTime);
                currentTime = utils.setMinutes(currentTime, currentMinutes + steps);
            }
        }
        return dates;
    }, [utils, steps, maxTime, minTime, customTimes]);
    // Check if inputValue is in times array. If not, we will set selected time to first item in times array. (see render method)
    var isAnyDateFromTimesSelected = useMemo(function () { return times.indexOf(inputValue) > -1; }, [times, inputValue]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Rifm, { value: inputValue, onChange: onChange, format: formatter }, function (item) { return (React.createElement(TextField, __assign({}, props, { value: item.value, onChange: item.onChange, InputProps: __assign(__assign({}, InputProps), { endAdornment: (React.createElement(InputAdornment, { position: "end" },
                    React.createElement(Button, { disabled: props.disabled, "aria-label": "button-select-time", icon: true, color: "tertiary", onClick: handleOpen },
                        React.createElement(InputAdornmentIcon, null)))) }), onKeyDown: onKeyDown !== null && onKeyDown !== void 0 ? onKeyDown : handleKeyDown }))); }),
        React.createElement(SMenu, __assign({}, MenuProps, { 
            // classes={{ ...MenuProps.classes }}
            anchorEl: anchorElement, open: Boolean(anchorElement), onClose: handleClose, PaperProps: { style: { width: anchorElementWidth } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }), times.map(function (time, index) { return (React.createElement(MenuItem, { className: clsx(isAnyDateFromTimesSelected
                ? time === inputValue && 'Mui-selected'
                : index === 0 && 'Mui-selected'), key: time + index, onClick: handleMenuClick(time) }, time)); }))));
};
export default TimePicker;
